import { z } from 'zod';


export const ValidateRequestIdRequestSchema = z.object({
  requestId: z.string().min(1, 'ID de solicitud requerido'),
});

export const ValidateRequestIdResponseSchema = z.object({
  status: z.boolean(),
  email_masked: z.string().optional(),
  requestId: z.string().optional(),
  message: z.string().optional(),
}).passthrough();

export const ValidateEmailRequestSchema = z.object({
  email: z.string().min(1, 'Correo electrónico requerido'),
});

export const ValidateEmailResponseSchema = z.object({
  status: z.boolean(),
  requestId: z.string().optional(),
  hasProgress: z.boolean().optional(),
  message: z.string().optional(),
}).passthrough();

export const ValidateEmailForRequestSchema = z.object({
  requestId: z.string().min(1, 'ID de solicitud requerido'),
  email: z.string().min(1, 'Correo electrónico requerido'),
});

export const ValidateEmailForRequestResponseSchema = z.object({
  status: z.boolean(),
  valid: z.boolean().optional(),
  message: z.string().optional(),
}).passthrough();

export const LoginWithIdEmailRequestSchema = z.object({
  requestId: z.string().min(1, 'ID de solicitud requerido'),
  email: z.string().min(1, 'Correo electrónico requerido'),
});

export const LoginWithIdEmailResponseSchema = z.object({
  success: z.boolean(),
  token: z.string().optional(),
  data: z.any().optional(),
  message: z.string().optional(),
}).passthrough();


export const SendOtpRequestSchema = z.object({
  requestId: z.string().min(1, 'ID de solicitud requerido'),
  email: z.string().min(1, 'Correo electrónico requerido'),
  skipCooldown: z.boolean().optional(),
});

export const SendOtpResponseSchema = z.object({
  status: z.boolean(),
  message: z.string(),
}).passthrough();

export const VerifyOtpRequestSchema = z.object({
  requestId: z.string().min(1, 'ID de solicitud requerido'),
  email: z.string().min(1, 'Correo electrónico requerido'),
  otp: z.string().min(1, 'Código OTP requerido'),
});

export const VerifyOtpResponseSchema = z.object({
  status: z.boolean(),
  message: z.string(),
  token: z.string().optional(),
}).passthrough();


export const ReturnableCertificateSchema = z.object({
  id: z.union([z.string(), z.number()]),
  number: z.string().optional(),
  office: z.union([
    z.string(),
    z.object({
      description: z.string().optional(),
      value: z.string().optional(),
    }).passthrough(),
  ]).optional(),
  matricula: z.string().optional(),
  status: z.string().optional(),
  idState: z.coerce.number().optional(),
  canReturn: z.boolean().optional(),
  message: z.string().optional(),
  pin: z.string().nullable().optional(),
  value: z.coerce.number().optional(),
}).passthrough();

export const GetReturnableCertificatesRequestSchema = z.object({
  requestId: z.string().optional(),
  email: z.string().optional(),
  token: z.string().min(1, 'Token de sesión requerido'),
});

export const GetReturnableCertificatesResponseSchema = z.object({
  status: z.boolean(),
  data: z.array(ReturnableCertificateSchema).optional(),
  message: z.string().optional(),
}).passthrough();

export const ResumeSessionRequestSchema = z.object({
  token: z.string().min(1, 'Token de sesión requerido'),
});

export const ResumeSessionResponseSchema = z.object({
  status: z.boolean(),
  data: z.any().optional(),
  message: z.string().optional(),
}).passthrough();

export const CancelSessionRequestSchema = z.object({
  requestId: z.string().min(1, 'ID de solicitud requerido'),
  token: z.string().min(1, 'Token de sesión requerido'),
});

export const CancelSessionResponseSchema = z.object({
  status: z.boolean(),
  message: z.string(),
}).passthrough();

export const ValidateSelectionRequestSchema = z.object({
  requestId: z.string().min(1, 'ID de solicitud requerido'),
  selectedCertIds: z.array(z.coerce.string()),
  token: z.string().min(1, 'Token de sesión requerido'),
});

export const ValidateSelectionResponseSchema = z.object({
  status: z.boolean(),
  eligible: z.array(z.union([z.string(), z.number()])).optional(),
  ineligible: z.array(z.union([z.string(), z.number()])).optional(),
  message: z.string().optional(),
}).passthrough();

export const ConfirmSelectionRequestSchema = z.object({
  requestId: z.string().min(1, 'ID de solicitud requerido'),
  selectedCertIds: z.array(z.coerce.string()),
  token: z.string().min(1, 'Token de sesión requerido'),
});

export const ConfirmSelectionResponseSchema = z.object({
  status: z.boolean(),
  data: z.any().optional(),
  token: z.string().optional(),
  message: z.string().optional(),
}).passthrough();


export const BankItemSchema = z.object({
  name: z.string(),
  code: z.string().optional(),
}).passthrough();

export const GetBanksResponseSchema = z.object({
  status: z.boolean(),
  data: z.array(BankItemSchema),
}).passthrough();

export const BankDataSchema = z.object({
  personType: z.string().optional(),
  ownerName: z.string().optional(),
  ownerLastname: z.string().optional(),
  docType: z.string().optional(),
  docNumber: z.string().optional(),
  bank: z.string().optional(),
  accountType: z.string().optional(),
  accountNumber: z.string().optional(),
  confirmAccountNumber: z.string().optional(),
  cellphone: z.string().optional(),
  email: z.string().optional(),
  filePath: z.string().nullable().optional(),
  useExistingData: z.boolean().optional(),
}).passthrough();

export const SaveProgressRequestSchema = z.object({
  requestId: z.string().min(1, 'ID de solicitud requerido'),
  bankData: BankDataSchema,
  token: z.string().min(1, 'Token de sesión requerido'),
});

export const SaveProgressResponseSchema = z.object({
  status: z.boolean(),
  message: z.string(),
  token: z.string().optional(),
}).passthrough();

export const UploadFileResponseSchema = z.object({
  status: z.boolean(),
  message: z.string(),
  filePath: z.string().optional(),
  error: z.any().optional(),
}).passthrough();

export const FinalizeReturnRequestSchema = z.object({
  requestId: z.string().min(1, 'ID de solicitud requerido'),
  bankData: BankDataSchema,
  token: z.string().min(1, 'Token de sesión requerido'),
});

export const FinalizeReturnResponseSchema = z.object({
  status: z.boolean(),
  message: z.string(),
  completed: z.boolean().optional(),
  sessionExpired: z.boolean().optional(),
}).passthrough();

export type ValidateRequestIdRequest = z.infer<typeof ValidateRequestIdRequestSchema>;
export type ValidateRequestIdResponse = z.infer<typeof ValidateRequestIdResponseSchema>;
export type ValidateEmailRequest = z.infer<typeof ValidateEmailRequestSchema>;
export type ValidateEmailResponse = z.infer<typeof ValidateEmailResponseSchema>;
export type ValidateEmailForRequest = z.infer<typeof ValidateEmailForRequestSchema>;
export type ValidateEmailForRequestResponse = z.infer<typeof ValidateEmailForRequestResponseSchema>;
export type LoginWithIdEmailRequest = z.infer<typeof LoginWithIdEmailRequestSchema>;
export type LoginWithIdEmailResponse = z.infer<typeof LoginWithIdEmailResponseSchema>;
export type SendOtpRequest = z.infer<typeof SendOtpRequestSchema>;
export type SendOtpResponse = z.infer<typeof SendOtpResponseSchema>;
export type VerifyOtpRequest = z.infer<typeof VerifyOtpRequestSchema>;
export type VerifyOtpResponse = z.infer<typeof VerifyOtpResponseSchema>;
export type ReturnableCertificate = z.infer<typeof ReturnableCertificateSchema>;
export type GetReturnableCertificatesRequest = z.infer<typeof GetReturnableCertificatesRequestSchema>;
export type GetReturnableCertificatesResponse = z.infer<typeof GetReturnableCertificatesResponseSchema>;
export type ResumeSessionRequest = z.infer<typeof ResumeSessionRequestSchema>;
export type ResumeSessionResponse = z.infer<typeof ResumeSessionResponseSchema>;
export type CancelSessionRequest = z.infer<typeof CancelSessionRequestSchema>;
export type CancelSessionResponse = z.infer<typeof CancelSessionResponseSchema>;
export type ValidateSelectionRequest = z.infer<typeof ValidateSelectionRequestSchema>;
export type ValidateSelectionResponse = z.infer<typeof ValidateSelectionResponseSchema>;
export type ConfirmSelectionRequest = z.infer<typeof ConfirmSelectionRequestSchema>;
export type ConfirmSelectionResponse = z.infer<typeof ConfirmSelectionResponseSchema>;
export type BankItem = z.infer<typeof BankItemSchema>;
export type GetBanksResponse = z.infer<typeof GetBanksResponseSchema>;
export type BankData = z.infer<typeof BankDataSchema>;
export type SaveProgressRequest = z.infer<typeof SaveProgressRequestSchema>;
export type SaveProgressResponse = z.infer<typeof SaveProgressResponseSchema>;
export type UploadFileResponse = z.infer<typeof UploadFileResponseSchema>;
export type FinalizeReturnRequest = z.infer<typeof FinalizeReturnRequestSchema>;
export type FinalizeReturnResponse = z.infer<typeof FinalizeReturnResponseSchema>;
